import { useCallback, useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { MediaTypes, useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { validateImageSize } from '#src/app/pages/startAPetitionV2/shared/utils';
import type { MediaCropArea } from '#src/app/shared/hooks/imageUpload';
import type { UploaderControls } from '#src/app/shared/hooks/uppyUploader';
import { UPLOAD_ERROR_MESSAGES, useUploader } from '#src/app/shared/hooks/uppyUploader';
import { isLoaded, isLoading } from '#src/app/shared/utils/async';

const MAX_IMAGE_FILE_SIZE = 15 * 1024 * 1024; // 15MB

type Data = {
	image?: string;
	imageError: TranslationKey | null;
	uploadError: TranslationKey | null;
	uploader: UploaderControls;
	isUploading: boolean;
	imageData?: { data: string; status: 'added' | 'saved' };
};
type Actions = {
	handleImageChange: (image?: string) => void;
	handleImageCrop: (croppingData?: MediaCropArea) => void;
	handleCancelUpload: () => void;
	handleUploadClick: () => void;
	submit: () => Promise<void>;
	back: () => void;
};
type UseUploaderCropperFormReturn = ModelHookResult<Data, Actions>;

export function useUploaderCropperForm(): UseUploaderCropperFormReturn {
	const { actions, data } = useStartAPetitionPageState();
	const utilityContext = useUtilityContext();
	const track = useTracking();

	const initialImage =
		data.draft?.petitionDraft?.image?.type === MediaTypes.USER ? data.draft.petitionDraft.image.url : undefined;

	const [imageData, setImageData] = useState<{ data: string; status: 'added' | 'saved' } | undefined>(
		initialImage ? { data: initialImage, status: 'saved' } : undefined,
	);
	const [imageError, setImageError] = useState<TranslationKey | null>(null);
	const [uploadError, setUploadError] = useState<TranslationKey | null>(null);

	const uploader = useUploader(setImageError, 1, MAX_IMAGE_FILE_SIZE);

	/* Uploader Handlers */
	const onImageChange = useCallback((image?: string) => {
		if (!image) return;

		setImageError(null);
		setUploadError(null);
		setImageData({ data: image, status: 'added' });
	}, []);

	const onImageCrop = useCallback((croppingData?: MediaCropArea) => {
		if (!croppingData) return;
		// this checks the image size to suggest using a larger image if the user uploads a small image
		// its not a restriction like the errors handled in `uppy`
		const isValidImageSize = validateImageSize(croppingData);
		if (!isValidImageSize) setImageError(UPLOAD_ERROR_MESSAGES.IMAGE_TOO_SMALL);
	}, []);

	const onCancelUpload = useCallback(() => {
		void track('sap_media_remove_click');

		setImageError(null);
		setUploadError(null);
		setImageData(undefined);
	}, [track]);

	const onUploadClick = useCallback(() => {
		void track('sap_media_upload_click');
	}, [track]);

	/* StepActions Handlers */
	const { mutateAsync, ...mutationState } = useMutation({
		// oxlint-disable-next-line complexity
		mutationFn: async () => {
			const result = await uploader.upload();

			if ((result?.failed?.length ?? 0) > 0) {
				throw new Error(result?.failed?.[0]?.error ?? 'Upload failed');
			}

			if (!result?.successful?.[0]?.uploadURL) {
				throw new Error('Bad URL returned from upload');
			}

			return result.successful[0];
		},
	});
	const submit = useCallback(async () => {
		void track('sap_media_submit');
		try {
			if (imageData && imageData.status !== 'saved') {
				const result = await mutateAsync();
				setImageError(null);
				setUploadError(null);
				const uploadURL = result.uploadURL ?? '';
				setImageData({ data: uploadURL, status: 'saved' });

				void track('sap_media_upload_success');

				actions.send({
					type: 'submit',
					draft: {
						petitionDraft: { image: { url: uploadURL, type: MediaTypes.USER } },
						metadata: { imageJobId: null },
					},
				});
			} else {
				actions.send({ type: 'submit' });
			}
		} catch (e) {
			void track('sap_media_upload_error');

			setUploadError(UPLOAD_ERROR_MESSAGES.CATCH_ALL);
		}
	}, [actions, mutateAsync, track, imageData]);

	const back = useCallback(() => {
		void track('sap_media_back');

		actions.send({ type: 'back' });
	}, [actions, track]);

	useEffect(() => {
		uploader.onFileAdded(onImageChange);
		uploader.onUseDropTarget();
		uploader.onUseAwsS3(utilityContext, 'USER_UPLOADS');
	}, []);

	return {
		data: {
			image: imageData?.data,
			imageData,
			uploader,
			imageError,
			uploadError,
			isUploading: isLoading(mutationState) || isLoaded(mutationState),
		},
		actions: {
			handleImageChange: onImageChange,
			handleImageCrop: onImageCrop,
			handleCancelUpload: onCancelUpload,
			handleUploadClick: onUploadClick,
			submit,
			back,
		},
	};
}
